import { useState, useContext, useEffect } from 'react'
import { ChevronDownIcon } from '@heroicons/react/24/solid'
import ButtonEl from '../helpers/ctas/Button'
import { MarkupContext } from '../../providers/Markup'
import { dataLayerPush } from '../../helpers/thirdparty'

/**
 * MobileMegaMenuNav - Mobile version of data-sourced mega menu navigation
 * Uses headerDataSource.megaMenuColumns structure from Umbraco
 */

const MobileMegaMenuSection = ({ section, active, category }) => {
  const hasLevel3 = section.menuLinkL3?.length > 0
  const [isL2Expanded, setIsL2Expanded] = useState(false)

  // WCAG: Ensure L2 closes if the parent L1 is closed
  useEffect(() => {
    if (!active) {
      setIsL2Expanded(false)
    }
  }, [active])

  return (
    <div className="mega-menu-section w-full">
      {/* Level 2 - Main section link */}
      {section.menuLinkL2 && (
        <div
          className={`flex items-center relative w-full px-9 py-3 ${isL2Expanded ? 'border-b border-(--sb-mega-menu-mobile-list-expanded-border)' : ''}`}
        >
          <a
            href={section.menuLinkL2.link?.url}
            target={section.menuLinkL2.link?.target ?? '_self'}
            className={`
              text-sm
              not-italic
              font-medium
              text-(--sb-mega-menu-mobile-list-text)
              hover:text-(--sb-mega-menu-mobile-list-text-hover)
              leading-5
              transition-colors
              focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--sb-focus-ring-color) rounded
            `}
            tabIndex={active ? 0 : -1}
            onClick={() => {
              dataLayerPush({
                event: 'navigation_menu_click',
                category: category,
                click_text: section.menuLinkL2.link?.name,
              })
            }}
          >
            {section.menuLinkL2.link?.name}
          </a>

          {/* Level 2 Arrow Button - Massive Touch Target on the Right */}
          {hasLevel3 && (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                setIsL2Expanded(!isL2Expanded)
              }}
              // w-16 creates a large 64px wide touch target so fingers don't miss it
              className="absolute right-0 top-0 bottom-0 w-16 flex items-center justify-center z-10 text-(--sb-mega-menu-toggle-icon-color) focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-(--sb-focus-ring-color)"
              aria-expanded={isL2Expanded}
              tabIndex={active ? 0 : -1}
            >
              <span className="sr-only">Toggle sub-menu for {section.menuLinkL2.link?.name}</span>
              <ChevronDownIcon
                className={`h-5 w-5 transition-transform text-(--sb-mega-menu-chevron-down-icon-color) duration-300 ${isL2Expanded ? 'rotate-180' : ''}`}
              />
            </button>
          )}
        </div>
      )}

      {/* Level 3 - Child menu links */}
      {/* WCAG: Using 'invisible' ensures screen readers completely skip hidden items */}
      {hasLevel3 && (
        <ul
          className={`
          flex flex-col items-center w-full overflow-hidden transition-all duration-300
          ${isL2Expanded ? 'max-h-[3000px] opacity-100 visible py-2 px-12' : 'max-h-0 opacity-0 invisible'}
        `}
        >
          {section.menuLinkL3.map((link, idx) => (
            <li key={`child-${idx}`} className="w-full">
              <a
                href={link.link?.url}
                target={link.link?.target ?? '_self'}
                className={`
                  block
                  py-2.5
                  px-6
                  text-sm
                  not-italic
                  font-medium
                  text-(--sb-mega-menu-mobile-list-text)
                  hover:text-(--sb-mega-menu-mobile-list-text-hover)
                  leading-5
                  transition-colors
                  focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--sb-focus-ring-color) rounded
                `}
                tabIndex={active && isL2Expanded ? 0 : -1}
                onClick={() => {
                  dataLayerPush({
                    event: 'navigation_menu_click',
                    category: category,
                    click_text: link.link?.name,
                  })
                }}
              >
                {link.link?.name}
              </a>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

const MobileMegaMenuColumn = ({ column, idx, isOpen, onToggle }) => {
  const { detectPaletteClass } = useContext(MarkupContext)

  // Get the main menu link from the column (Level 1)
  const mainLink = column.mainMenuLink
  const hasChildLinks = column.sections?.some((section) => section.menuLinkL2 || section.menuLinkL3?.length > 0)

  return (
    <li className="w-full">
      <div
        className={`flex items-center relative w-full px-6 py-4 ${isOpen ? 'border-b border-(--sb-mega-menu-mobile-list-expanded-border)' : ''}`}
      >
        {mainLink ? (
          <>
            {/* The main navigation text Link - ONLY navigates now */}
            <a
              href={mainLink?.link?.url ?? '#'}
              target={mainLink?.link?.target ?? '_self'}
              className={detectPaletteClass(`
                text-base 
                font-medium
                !text-(--sb-mega-menu-mobile-list-text)
                hover:text-(--sb-mega-menu-mobile-list-text-hover)
                transition-colors
                focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--sb-focus-ring-color) rounded
                leading-6
                ${isOpen ? 'text-(--sb-mega-menu-mobile-list-text-hover)' : ''}
              `)}
              aria-haspopup={hasChildLinks ? 'true' : 'false'}
              aria-expanded={hasChildLinks ? (isOpen ? 'true' : 'false') : undefined}
              onClick={() => {
                dataLayerPush({
                  event: 'navigation_menu_click',
                  category: mainLink?.link?.name ?? '',
                  click_text: mainLink?.link?.name ?? '',
                })
              }}
            >
              {mainLink?.link?.name ?? ''}
            </a>

            {/* The Arrow button - Massive Touch Target on the Right */}
            {hasChildLinks && (
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  onToggle(idx)
                }}
                // w-16 creates a large 64px wide touch target so fingers don't miss it
                className="absolute right-0 top-0 bottom-0 w-16 flex items-center justify-center z-10 text-(--sb-mega-menu-chevron-down-icon-color) focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-(--sb-focus-ring-color)"
              >
                <span className="sr-only">Expand menu for {mainLink?.link?.name ?? ''}</span>
                <ChevronDownIcon
                  className={`h-4 w-4 transition-transform text-(--sb-mega-menu-chevron-down-icon-color) duration-300 ${isOpen ? 'rotate-180' : ''}`}
                />
              </button>
            )}
          </>
        ) : (
          <ButtonEl
            theme="none"
            idx={idx}
            className={detectPaletteClass(`
              w-full 
              flex 
              items-center 
              justify-center
              font-base 
              font-medium
              text-(--sb-mega-menu-mobile-list-text)!
              hover:text-(--sb-mega-menu-mobile-list-text-hover)
              transition-colors
              focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--sb-focus-ring-color) rounded
              leading-6
            `)}
            ariaHasPopup="true"
            ariaExpanded={isOpen ? 'true' : 'false'}
            onClick={() => onToggle(idx)}
            buttonType="default"
          >
            <span>Menu</span>
            <ChevronDownIcon
              className={`absolute right-6 h-4 w-4 transition-transform text-(--sb-mega-menu-chevron-down-icon-color) duration-300 ${isOpen ? 'rotate-180' : ''}`}
            />
          </ButtonEl>
        )}
      </div>

      {/* Expandable Panel */}
      <div
        id={`mobile-megamenu-col-${idx}`}
        className={`
          flex flex-col items-center w-full overflow-hidden transition-all duration-300
          ${isOpen ? 'max-h-[3000px] opacity-100 visible py-2' : 'max-h-0 opacity-0 invisible'}
        `}
      >
        {column.sections?.map((section, sIdx) => (
          <MobileMegaMenuSection
            key={`section-${sIdx}`}
            section={section}
            category={mainLink?.link?.name ?? ''}
            active={isOpen}
          />
        ))}

        {/* CTA Image (if provided) */}
        {column.megaMenuCtaImage && (
          <div className="p-6 w-full max-w-sm mx-auto">
            {column.navigationImageLink ? (
              <a
                href={column.navigationImageLink.link?.url}
                target={column.navigationImageLink.link?.target ?? '_self'}
                className="block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--sb-focus-ring-color) rounded-lg"
                tabIndex={isOpen ? 0 : -1}
              >
                <img
                  src={column.megaMenuCtaImage.src}
                  alt={column.megaMenuCtaImage.altText || ''}
                  className="w-full h-auto rounded-lg shadow-sm"
                />
                {column.navigationImageDescription && (
                  <p className="mt-4 text-sm text-center leading-6  text-(--sb-mega-menu-list-text)">
                    {column.navigationImageDescription}
                  </p>
                )}
              </a>
            ) : (
              <>
                <img
                  src={column.megaMenuCtaImage.src}
                  alt={column.megaMenuCtaImage.altText || ''}
                  className="w-full h-auto rounded-lg shadow-sm"
                />
                {column.navigationImageDescription && (
                  <p className="mt-4 text-sm text-center leading-6  text-(--sb-mega-menu-list-text)">
                    {column.navigationImageDescription}
                  </p>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </li>
  )
}

const MobileMegaMenuNav = (props) => {
  const [activeColumn, setActiveColumn] = useState(null)
  const { megaMenuColumns } = props

  const handleToggle = (idx) => {
    setActiveColumn((prev) => (prev === idx ? null : idx))
  }

  if (!megaMenuColumns?.length) {
    return null
  }

  return (
    <nav data-type="mobile-megamenu-navigation" className="w-full">
      <ul className="flex flex-col items-start w-full gap-2">
        {megaMenuColumns.map((column, idx) => (
          <MobileMegaMenuColumn
            key={`col-${idx}`}
            column={column}
            idx={idx}
            isOpen={activeColumn === idx}
            onToggle={handleToggle}
          />
        ))}
      </ul>
    </nav>
  )
}

export default MobileMegaMenuNav
